import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, PenLine } from "lucide-react";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductReviews({ productId, productName }) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const wantsReview = searchParams.get("review") === "1";
  const sectionRef = useRef(null);
  const [reviews, setReviews] = useState([]);
  const [avg, setAvg] = useState(null);
  const [count, setCount] = useState(0);
  const [eligibility, setEligibility] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ rating: 5, title: "", body: "" });
  const [submitting, setSubmitting] = useState(false);

  const loadReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews?product_id=${productId}`);
      setReviews(data.reviews || []);
      setAvg(data.average);
      setCount(data.count || 0);
    } catch {
      setReviews([]);
    }
  };

  const loadEligibility = async () => {
    if (!user) {
      setEligibility(null);
      return;
    }
    try {
      const { data } = await axios.get(
        `${API}/reviews/eligibility?product_id=${productId}`
      );
      setEligibility(data);
    } catch {
      setEligibility(null);
    }
  };

  useEffect(() => {
    loadReviews();
  }, [productId]);

  useEffect(() => {
    loadEligibility();
  }, [productId, user?.user_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || form.body.trim().length < 4) {
      toast.error("Please give it a title and a few words.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        product_id: productId,
        rating: form.rating,
        title: form.title.trim(),
        body: form.body.trim(),
      });
      toast.success("Thank you — your review is live.");
      setForm({ rating: 5, title: "", body: "" });
      setShowForm(false);
      await loadReviews();
      await loadEligibility();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit review.");
    } finally {
      setSubmitting(false);
    }
  };

  // Deep-link support: /product/:id?review=1 (from post-fulfillment email)
  // Auto-opens the review form + scrolls when eligibility check confirms the user can review.
  useEffect(() => {
    if (!wantsReview || !eligibility) return;
    if (eligibility.can_review) {
      setShowForm(true);
      // Scroll into view after render
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
    // Clear the query param so refresh doesn't retrigger
    setSearchParams({}, { replace: true });
  }, [wantsReview, eligibility, setSearchParams]);

  return (
    <section
      ref={sectionRef}
      data-testid="product-reviews"
      className="border-t hairline-light py-16 md:py-24 bg-[color:var(--sr-bg)]"
    >
      <div className="sr-container">
        <div className="grid grid-cols-12 gap-6 md:gap-12 items-end mb-12">
          <div className="col-span-12 md:col-span-7">
            <p className="label-eyebrow text-[color:var(--sr-primary)] mb-4 flex items-center gap-3">
              <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> Reviews
            </p>
            <h3 className="font-serif text-4xl md:text-6xl leading-[0.95] tracking-tight text-[color:var(--sr-primary)]">
              What people say about
              <br />
              <em className="italic font-light text-[color:var(--sr-accent)]">
                {productName}.
              </em>
            </h3>
          </div>
          <div className="col-span-12 md:col-span-5 md:text-right">
            {count > 0 ? (
              <>
                <p className="font-serif text-6xl text-[color:var(--sr-primary)] tabular-nums">
                  {avg?.toFixed(1) || "—"}
                </p>
                <div className="flex items-center gap-1 md:justify-end text-[color:var(--sr-accent)] mt-1">
                  {Array.from({ length: Math.round(avg || 0) }).map((_, i) => (
                    <Star key={i} size={14} strokeWidth={0} fill="currentColor" />
                  ))}
                </div>
                <p className="text-xs text-[color:var(--sr-muted)] mt-2">
                  {count} verified {count === 1 ? "review" : "reviews"}
                </p>
              </>
            ) : (
              <p className="text-sm text-[color:var(--sr-muted)] leading-relaxed">
                Be the first to share your thoughts on {productName}.
              </p>
            )}
          </div>
        </div>

        {/* Write-a-review CTA / form */}
        {user ? (
          eligibility?.can_review ? (
            !showForm ? (
              <button
                type="button"
                onClick={() => setShowForm(true)}
                data-testid="write-review-btn"
                className="inline-flex items-center gap-3 px-6 py-3 border hairline hover:border-[color:var(--sr-primary)] label-eyebrow text-[color:var(--sr-primary)] transition-colors mb-12"
              >
                <PenLine size={14} strokeWidth={1.5} /> Write a review
              </button>
            ) : (
              <ReviewForm
                form={form}
                setForm={setForm}
                submit={submit}
                submitting={submitting}
                onCancel={() => setShowForm(false)}
              />
            )
          ) : eligibility?.already_reviewed ? (
            <p
              data-testid="already-reviewed"
              className="text-xs uppercase tracking-[0.24em] text-[color:var(--sr-muted)] mb-12"
            >
              You&apos;ve already reviewed this — thank you.
            </p>
          ) : (
            <p
              data-testid="not-purchased"
              className="text-xs uppercase tracking-[0.24em] text-[color:var(--sr-muted)] mb-12"
            >
              Reviews are for verified purchasers.
            </p>
          )
        ) : (
          <p className="text-xs uppercase tracking-[0.24em] text-[color:var(--sr-muted)] mb-12">
            <Link to="/login" className="underline decoration-[color:var(--sr-accent)] text-[color:var(--sr-primary)]">
              Sign in
            </Link>{" "}
            to write a review.
          </p>
        )}

        {/* Review list */}
        {reviews.length > 0 ? (
          <ul
            className="divide-y divide-[color:var(--sr-border-light)]"
            data-testid="product-reviews-list"
          >
            {reviews.map((r, idx) => (
              <motion.li
                key={r.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: idx * 0.05 }}
                className="py-8 grid grid-cols-12 gap-6"
                data-testid={`review-item-${r.id}`}
              >
                <div className="col-span-12 md:col-span-3">
                  <div className="flex items-center gap-1 text-[color:var(--sr-accent)] mb-2">
                    {Array.from({ length: r.rating || 5 }).map((_, i) => (
                      <Star key={i} size={12} strokeWidth={0} fill="currentColor" />
                    ))}
                  </div>
                  <p className="font-serif text-lg text-[color:var(--sr-primary)] leading-tight">
                    {r.name}
                  </p>
                  <p className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--sr-muted)] mt-1">
                    {r.verified_purchase && "Verified · "}
                    {new Date(r.created_at).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                    })}
                  </p>
                </div>
                <div className="col-span-12 md:col-span-9">
                  <p className="font-serif text-2xl text-[color:var(--sr-primary)] leading-tight mb-2">
                    {r.title}
                  </p>
                  <p className="text-[color:var(--sr-text)]/85 leading-relaxed">
                    {r.body}
                  </p>
                </div>
              </motion.li>
            ))}
          </ul>
        ) : (
          <div className="border hairline p-12 text-center">
            <p className="font-serif text-3xl text-[color:var(--sr-primary)]">
              No reviews yet.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

function ReviewForm({ form, setForm, submit, submitting, onCancel }) {
  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="border hairline p-8 md:p-10 mb-12 max-w-2xl"
      data-testid="review-form"
    >
      <p className="label-eyebrow text-[color:var(--sr-primary)] mb-6 flex items-center gap-3">
        <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> Your review
      </p>
      <div className="mb-6">
        <span className="label-eyebrow text-[color:var(--sr-muted)] mb-3 block">Rating</span>
        <div className="flex items-center gap-1" data-testid="rating-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              type="button"
              key={n}
              onClick={() => setForm({ ...form, rating: n })}
              data-testid={`star-${n}`}
              className="p-1"
              aria-label={`${n} stars`}
            >
              <Star
                size={28}
                strokeWidth={1.25}
                className={n <= form.rating ? "text-[color:var(--sr-accent)]" : "text-[color:var(--sr-border)]"}
                fill={n <= form.rating ? "currentColor" : "none"}
              />
            </button>
          ))}
        </div>
      </div>
      <label className="block mb-5">
        <span className="label-eyebrow text-[color:var(--sr-muted)] mb-2 block">Title</span>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          data-testid="review-title"
          maxLength={120}
          required
          className="w-full bg-transparent border-b hairline focus:border-[color:var(--sr-primary)] outline-none py-2 text-[color:var(--sr-primary)] font-serif text-xl"
        />
      </label>
      <label className="block mb-8">
        <span className="label-eyebrow text-[color:var(--sr-muted)] mb-2 block">Your thoughts</span>
        <textarea
          rows={4}
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          data-testid="review-body"
          minLength={4}
          maxLength={2000}
          required
          className="w-full bg-transparent border hairline focus:border-[color:var(--sr-primary)] outline-none p-3 text-[color:var(--sr-primary)] text-base leading-relaxed resize-y"
        />
      </label>
      <div className="flex items-center gap-3 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="label-eyebrow px-6 py-3 text-[color:var(--sr-primary)] hover:opacity-70"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          data-testid="submit-review-btn"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] label-eyebrow hover:bg-[color:var(--sr-primary-deep)] transition-colors disabled:opacity-40"
        >
          {submitting ? "Posting…" : "Post review"}
        </button>
      </div>
    </motion.form>
  );
}
